import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  type BasicTeammate,
} from "@repo/app-types/entities/teammate";
import {
  getGradingDisplayNames,
  type TeammateAccountGrading,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { ShortAbsoluteTimestamp } from "@repo/ui/timestamps/shortAbsoluteTimestamp";
import type { FC } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { renderGradingValue } from "./gradingHelpers.js";

export interface CriteriaScoreCardProps {
  grading: TeammateAccountGrading;
  criterion: PlaybookCriterionNode;
  teammate: BasicTeammate;
  to: string;
}

export const CriteriaScoreCard: FC<CriteriaScoreCardProps> = (props) => {
  const { grading, criterion, teammate, to } = props;
  const advisorName = buildTeammateDisplayNames(teammate).primaryDisplayName;
  const customerName =
    getGradingDisplayNames(grading).primaryDisplayName ?? grading.name;
  const score = renderGradingValue(
    criterion.grading_value_type,
    grading.assessment?.criteria_grading[criterion.id]?.value,
    { showLabel: false },
  );

  return (
    <Link
      className="block w-[260px] shrink-0 rounded-md bg-white p-2.5 shadow-sm ring-1 ring-black/5 transition hover:bg-slate-50"
      to={to}
      target="_blank"
      rel="noreferrer"
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <div
            className="min-w-0 truncate text-sm font-medium text-foreground"
            title={advisorName}
          >
            {advisorName}
          </div>
          <div className="shrink-0">{score}</div>
        </div>
        <div className="flex min-w-0 items-center gap-x-1.5 text-xs text-muted-foreground">
          <span className="shrink-0">Call with</span>
          <div
            className={twMerge(
              "min-w-0 truncate text-xs text-foreground",
              PH_MASK_CLASS,
            )}
            title={customerName || "--"}
          >
            {customerName || "--"}
          </div>
          <span className="shrink-0 text-slate-400">&middot;</span>
          <div className="shrink-0">
            <ShortAbsoluteTimestamp timestamp={grading.last_recorded_at} />
          </div>
        </div>
      </div>
    </Link>
  );
};
